import { lazy, Suspense } from 'react';
import { ROUTES } from './routes-definitions';
import type { AppRoute } from '../types/router.types';
import { Navigate } from 'react-router';
import { ClusterDetailsSkeleton } from '../modules/cluster-details/components/cluster-details-skeleton';
import { ClusterFormSkeleton } from '../modules/cluster-form/components/cluster-form-skeleton';
import { ClusterInstallationsAddSkeleton } from '../modules/cluster-installations-add/components/cluster-installations-add-skeleton';
import { ClustersSkeleton } from '../modules/clusters/components/clusters-skeleton';
import { SettingsSkeleton } from '../modules/settings/components';
import { NotificationsSkeleton } from '../modules/notifications/components/notifications-skeleton';
import { ClusterDataSkeleton } from '../modules/cluster-data/components';
import { MapSkeleton } from '../modules/map/components/map-skeleton';
import { ClusterInstallationDetailsSkeleton } from '../modules/cluster-installation-details/components';
import { HomeSkeleton } from '../modules/home/components/home-skeleton';
import { ClusterDataProvider } from '../modules/cluster-data/context/provider';
import { ClusterInstallationMeterDataSkeleton } from '../modules/cluster-installation-meter-data/components';
import { MeterDataProvider } from '../modules/cluster-installation-meter-data/context/provider';
import { DocumentsSkeleton } from '../modules/documents/components';
import { ClusterDetailsProvider } from '../modules/cluster-details/context/provider';

const HomePage = lazy(() => import('../modules/home/home').then((module) => ({ default: module.Home })));
const SettingsPage = lazy(() =>
  import('../modules/settings/settings').then((module) => ({
    default: module.Settings,
  })),
);
const NotificationsPage = lazy(() =>
  import('../modules/notifications/notifications').then((module) => ({
    default: module.Notifications,
  })),
);
const ClustersPage = lazy(() =>
  import('../modules/clusters/clusters').then((module) => ({
    default: module.Clusters,
  })),
);
const ClusterDetailsPage = lazy(() =>
  import('../modules/cluster-details/cluster-details').then((module) => ({
    default: module.ClusterDetails,
  })),
);
const ClusterFormPage = lazy(() =>
  import('../modules/cluster-form/cluster-form').then((module) => ({
    default: module.ClusterForm,
  })),
);
const ClusterInstallationsAddPage = lazy(() =>
  import('../modules/cluster-installations-add/cluster-installations-add').then((module) => ({
    default: module.ClusterInstallationsAdd,
  })),
);
const ClusterInstallationDetailsPage = lazy(() =>
  import('../modules/cluster-installation-details/cluster-installation-details').then((module) => ({
    default: module.ClusterInstallationDetails,
  })),
);
const ClusterDataPage = lazy(() =>
  import('../modules/cluster-data/cluster-data').then((module) => ({
    default: module.ClusterData,
  })),
);
const MapPage = lazy(() =>
  import('../modules/map/map').then((module) => ({
    default: module.Map,
  })),
);
const ClusterInstallationMeterDataPage = lazy(() =>
  import('../modules/cluster-installation-meter-data/cluster-installation-meter-data').then((module) => ({
    default: module.ClusterInstallationMeterData,
  })),
);
const DocumentsPage = lazy(() =>
  import('../modules/documents/documents').then((module) => ({
    default: module.Documents,
  })),
);

export const routes: AppRoute[] = [
  {
    path: ROUTES.HOME,
    isProtected: false,
    adminRequired: false,
    element: (
      <Suspense fallback={<HomeSkeleton />}>
        <HomePage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.SETTINGS,
    isProtected: true,
    adminRequired: false,
    element: (
      <Suspense fallback={<SettingsSkeleton />}>
        <SettingsPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.DOCUMENTS,
    isProtected: true,
    adminRequired: false,
    element: (
      <Suspense fallback={<DocumentsSkeleton />}>
        <DocumentsPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.NOTIFICATIONS,
    isProtected: true,
    adminRequired: false,
    element: (
      <Suspense fallback={<NotificationsSkeleton />}>
        <NotificationsPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.CLUSTERS,
    isProtected: true,
    adminRequired: true,
    element: (
      <Suspense fallback={<ClustersSkeleton />}>
        <ClustersPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.CLUSTER_DETAILS,
    isProtected: true,
    adminRequired: true,
    element: (
      <Suspense fallback={<ClusterDetailsSkeleton />}>
        <ClusterDetailsProvider>
          <ClusterDetailsPage />
        </ClusterDetailsProvider>
      </Suspense>
    ),
  },
  {
    path: ROUTES.CLUSTER_CREATE,
    isProtected: true,
    adminRequired: true,
    element: (
      <Suspense fallback={<ClusterFormSkeleton />}>
        <ClusterFormPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.CLUSTER_EDIT,
    isProtected: true,
    adminRequired: true,
    element: (
      <Suspense fallback={<ClusterFormSkeleton />}>
        <ClusterFormPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.CLUSTER_INSTALLATIONS_ADD,
    isProtected: true,
    adminRequired: true,
    element: (
      <Suspense fallback={<ClusterInstallationsAddSkeleton />}>
        <ClusterInstallationsAddPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.CLUSTER_INSTALLATION_DETAILS,
    isProtected: true,
    adminRequired: true,
    element: (
      <Suspense fallback={<ClusterInstallationDetailsSkeleton />}>
        <ClusterInstallationDetailsPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.CLUSTER_DATA,
    isProtected: true,
    adminRequired: true,
    element: (
      <Suspense fallback={<ClusterDataSkeleton />}>
        <ClusterDataProvider>
          <ClusterDataPage />
        </ClusterDataProvider>
      </Suspense>
    ),
  },
  {
    path: ROUTES.MAP,
    isProtected: true,
    adminRequired: true,
    element: (
      <Suspense fallback={<MapSkeleton />}>
        <MapPage />
      </Suspense>
    ),
  },
  {
    path: ROUTES.CLUSTER_INSTALLATION_METER_DATA,
    isProtected: true,
    adminRequired: true,
    element: (
      <Suspense fallback={<ClusterInstallationMeterDataSkeleton />}>
        <MeterDataProvider>
          <ClusterInstallationMeterDataPage />
        </MeterDataProvider>
      </Suspense>
    ),
  },
  {
    path: '*',
    isProtected: false,
    adminRequired: false,
    element: <Navigate to={ROUTES.HOME} replace />,
  },
];
